import type {
  NavigationLinkKey,
  NavigationLinkItem,
  NavigationModelLinkKey,
} from "./navigation-link.type";
import type { FeatureFlagId } from "@/config/environment";

import { useTranslation } from "react-i18next";

import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { useAuthorization } from "@/modules/authorization";
import { getModelRoute } from "@/modules/model";
import ModelIcon from "@/modules/model-icon";

import {
  NAVIGATION_LINKS,
  NAVIGATION_MODEL_LINKS,
} from "./navigation-link.constant";

export const useNavigationLink = (
  key: NavigationLinkKey
): NavigationLinkItem | undefined => {
  const { t } = useTranslation();

  const {
    url,
    icon,
    external = false,
    featureFlag = "",
  } = NAVIGATION_LINKS[key];

  const isFeatureFlagActive = useFeatureFlag(featureFlag as FeatureFlagId);
  const showLink = featureFlag ? isFeatureFlagActive : true;

  if (!showLink) return undefined;

  const text = t(`navigation:${key}`);

  return {
    key,
    text,
    icon,
    url,
    external,
  };
};

export const useNavigationModelLink = (
  key: NavigationModelLinkKey
): NavigationLinkItem | undefined => {
  const { t } = useTranslation();
  const { canCreate, canView } = useAuthorization();

  const { modelKey, authorization } = NAVIGATION_MODEL_LINKS[key];
  const url = getModelRoute(modelKey);

  if (!url) return undefined;
  if (authorization === "view" && !canView(modelKey)) return undefined;
  if (authorization === "create" && !canCreate(modelKey)) return undefined;

  const text = t(`navigation:${key}`);
  const icon = <ModelIcon model={modelKey} />;
  const external = false;

  return {
    key,
    text,
    icon,
    url,
    external,
  };
};
